import React, { FunctionComponent, memo, useMemo } from "react";
import { Text, useColorModeValue } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { isEqual } from "lodash";

import { SiteConfig_siteConfig_dashboardConf_components } from "graphql/generated";

import StatsTable from "components/stats-table";
import BaseCard, { cardLabel, BaseCardProps } from "./BaseCard";
import OnOffStatusBadge from "scenes/site/components/on-off-status-badge";
import DisconnectPointStatus from "../../command/DisconnectPointStatus"
import CircuitBreakerStatus from "../../command/CircuitBreakerStatus"
import BessStatus from "../../command/BessStatus"


interface IProps extends BaseCardProps {
  baseUrl: string;
  data: any;
  config: SiteConfig_siteConfig_dashboardConf_components;
}


const ExternalCommandCard: FunctionComponent<IProps> = memo(function ExternalCommandCard({ data, config, baseUrl, ...rest }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const cardActive = useMemo(() => data?.control, [data?.control]);
  const lightIconUrl = useMemo(() => {
    switch (config.uiVersion) {
      case "opuncie": return "/images/svg/command-icon-opuncie-light.svg";
    }
  }, [config.uiVersion]);
  const darkIconUrl = useMemo(() => {
    switch (config.uiVersion) {
      case "opuncie": return "/images/svg/command-icon-opuncie-dark.svg";
    }
  }, [config.uiVersion]);
  const iconColorModeUrl = useColorModeValue(lightIconUrl, darkIconUrl);

  return (
    <BaseCard {...rest}
      title={intl.formatMessage({
        id: "dashboard__external_command____title",
        defaultMessage: `Aggregator {label}`,
      }, {
        ...cardLabel(config),
      })}
      onClick={() => {
        navigate(`${baseUrl}/command`);
      }}
      highlighted={cardActive}
      icon={cardActive ? iconColorModeUrl : undefined}
    >
        <StatsTable
          inline
          rows={[
            {
              label: intl.formatMessage({
                id: "dashboard__external_command__active_power",
                defaultMessage: "Active power",
              }),
              unit: "kW",
              decimals: 1,
              value: data?.active_power,
            },
            {
              label: intl.formatMessage({
                id: "dashboard__external_command__circuit_breaker_state",
                defaultMessage: "Circuit breaker",
              }),
              value: <CircuitBreakerStatus state={data?.cb_state} />,
            },
            {
              label: intl.formatMessage({
                id: "dashboard__external_command__circuit_breaker_command",
                defaultMessage: "... command | acknowledge",
              }),
              value: <>
                <OnOffStatusBadge state={data?.cb_command} colorOff="red" />
                <Text px={2}>
                  |
                </Text>
                <OnOffStatusBadge state={data?.cb_command_ack} colorOff="red" />
              </>,
            },
            {
              label: intl.formatMessage({
                id: "dashboard__external_command__bess_state",
                defaultMessage: "BESS",
              }),
              value: <BessStatus state={data?.bess_state} />,
            },
            {
              label: intl.formatMessage({
                id: "dashboard__external_command__bp_state",
                defaultMessage: "Disconnect point",
              }),
              value: <DisconnectPointStatus state={data?.bp_state} />,
            },
          ]}
        />
    </BaseCard>
  );
}, isEqual);

export default ExternalCommandCard;
